import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { RegistrationFormProps, RegistrationFormState } from "./registration-form.interface";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";

export const RegistrationForm = ({ onUserSubmitted }: RegistrationFormProps) => {
	const { t } = useTranslation(["common", "validation"]);

	const formik = useFormik<RegistrationFormState>({
		initialValues: {
			firstName: "",
			lastName: "",
			email: "",
			email2: "",
			password: "",
			password2: "",
		},
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			firstName: Yup.string().required(t("validation:firstNameRequired")),
			lastName: Yup.string().required(t("validation:lastNameRequired")),
			email: Yup.string()
				.email(t("validation:emailInvalid"))
				.required(t("validation:emailRequired")),
			email2: Yup.string()
				.email(t("validation:emailInvalid"))
				.oneOf([Yup.ref("email"), undefined], t("validation:emailMatch")),
			password: Yup.string()
				.min(8, t("validation:passwordMin", { min: 8 }))
				.required(t("validation:passwordRequired")),
			password2: Yup.string().oneOf(
				[Yup.ref("password"), undefined],
				t("validation:passwordMatch"),
			),
		}),
		onSubmit: (data, { setSubmitting }) => {
			onUserSubmitted(data, () => {
				setSubmitting(false);
			});
		},
	});

	return (
		<form onSubmit={formik.handleSubmit} className="w-12 p-fluid mb-5">
			<ValidatedField<RegistrationFormState, string>
				className="mb-4"
				name="firstName"
				label={t("common:firstName")}
				required
				formikConfig={formik}
				component={DefaultTextFieldComponent}
			/>
			<ValidatedField<RegistrationFormState, string>
				className="mb-4"
				name="lastName"
				label={t("common:lastName")}
				required
				formikConfig={formik}
				component={DefaultTextFieldComponent}
			/>
			<ValidatedField<RegistrationFormState, string>
				className="mb-4"
				name="email"
				label={t("common:email")}
				required
				formikConfig={formik}
				component={DefaultTextFieldComponent}
			/>
			<ValidatedField<RegistrationFormState, string>
				className="mb-4"
				name="email2"
				label={t("common:emailRepeat")}
				required
				formikConfig={formik}
				component={DefaultTextFieldComponent}
			/>
			<ValidatedField<RegistrationFormState, string>
				className="mb-4"
				name="password"
				label={t("common:passwordMin8")}
				required
				formikConfig={formik}
				component={({ fieldValue, updateField, fieldName, isValid }) => (
					<Password
						id={fieldName}
						name={fieldName}
						value={fieldValue}
						onChange={(e) => updateField(e.target.value)}
						toggleMask
						feedback={false}
						className={classNames({ "p-invalid": !isValid })}
					/>
				)}
			/>
			<ValidatedField<RegistrationFormState, string>
				className="mb-6"
				name="password2"
				label={t("common:passwordRepeat")}
				required
				formikConfig={formik}
				component={({ fieldValue, updateField, fieldName, isValid }) => (
					<Password
						id={fieldName}
						name={fieldName}
						value={fieldValue}
						onChange={(e) => updateField(e.target.value)}
						toggleMask
						feedback={false}
						className={classNames({ "p-invalid": !isValid })}
					/>
				)}
			/>
			<Button
				disabled={formik.isSubmitting}
				type="submit"
				label={t("common:createUserAndAdd")}
			/>
		</form>
	);
};
